import { API } from "./constants";

/**
 * Safety-net renderer: some clients (early iOS TestFlight build) mistakenly
 * post a photo as a plain text URL instead of a multipart file. This helper
 * detects those "text-only URL" messages and returns an object describing how
 * to render them inline (image / video / file) — without changing the DB.
 *
 * Returns null when the text is normal (no URL or a URL mixed with words) so
 * the caller falls back to standard text rendering.
 */
const IMG_EXT = /\.(jpe?g|png|webp|gif|heic|heif)(\?.*)?$/i;
const VID_EXT = /\.(mp4|mov|webm|m4v|3gp)(\?.*)?$/i;

// Patterns we consider "one of our attachments served by the backend"
const OUR_HOSTS_HINT = /\/api\/(chat\/attachments|files|documents|chantiers\/[^/]+\/documents)\//i;
const STORAGE_PATH = /^(chat|documents|debriefs|reports)\/[^\s]+\.[a-z0-9]{2,5}$/i;

export default function detectMediaUrl(text) {
  if (!text || typeof text !== "string") return null;
  const trimmed = text.trim();
  if (!trimmed) return null;

  // Reject messages that mix text + URL — user probably meant to keep the link
  // as-is. We only patch pure single-URL messages.
  if (trimmed.split(/\s+/).length !== 1) return null;

  let url = trimmed;

  // Case 1: bare storage path like "chat/xxx/abc.jpg" — prepend the API base
  if (STORAGE_PATH.test(url)) {
    url = `${API}/files/${url}`;
  }

  // Case 2: absolute or root-relative URL to our own attachment paths
  const isHttp = url.startsWith("http://") || url.startsWith("https://");
  const isApi = url.startsWith("/api/") && OUR_HOSTS_HINT.test(url);
  if (!isHttp && !isApi) return null;

  if (isApi) url = url.startsWith("/api") ? `${API.replace(/\/api$/, "")}${url}` : url;

  const isImage = IMG_EXT.test(url);
  const isVideo = VID_EXT.test(url);
  const isOurs = OUR_HOSTS_HINT.test(url) || isHttp && /bkpro\.app|preview\.emergentagent\.com|amazonaws|storage/i.test(url);

  if (!isImage && !isVideo) return null;
  if (!isOurs && !isImage && !isVideo) return null;

  return { url, kind: isImage ? "photo" : "video" };
}
